import { EMOTE_IDS, type EmoteId } from '@veriverse/shared';
import type Phaser from 'phaser';
import angry from '../assets/emotes/angry.png';
import down from '../assets/emotes/down.png';
import mail from '../assets/emotes/mail.png';
import sad from '../assets/emotes/sad.png';
import think from '../assets/emotes/think.png';
import timer from '../assets/emotes/timer.png';
import timerRed from '../assets/emotes/timer_red.png';
import up from '../assets/emotes/up.png';

/**
 * The emote sprites: LimeZu's animated UI speech bubbles (assets-src/LICENSES.md), converted from
 * the 32x32 GIFs to PNG strips of 32x64 frames.
 */
export const EMOTE_SHEETS: Record<EmoteId, { url: string; frames: number; fps: number }> = {
  angry: { url: angry, frames: 4, fps: 8 },
  sad: { url: sad, frames: 6, fps: 8 },
  think: { url: think, frames: 8, fps: 8 },
  mail: { url: mail, frames: 6, fps: 4 },
  up: { url: up, frames: 6, fps: 8 },
  down: { url: down, frames: 6, fps: 8 },
  timer: { url: timer, frames: 8, fps: 4 },
  timer_red: { url: timerRed, frames: 8, fps: 4 },
};

export const EMOTE_FRAME = { width: 32, height: 64 } as const;
/** Where the bubble's tail tip sits in the frame, as a fraction of its height. */
export const EMOTE_TAIL_ORIGIN = 0.8;

export const emoteTextureKey = (id: EmoteId): string => `emote:${id}`;
export const emoteAnimationKey = (id: EmoteId): string => `emote:${id}:play`;

export function loadEmotes(scene: Phaser.Scene): void {
  for (const id of EMOTE_IDS)
    scene.load.spritesheet(emoteTextureKey(id), EMOTE_SHEETS[id].url, {
      frameWidth: EMOTE_FRAME.width,
      frameHeight: EMOTE_FRAME.height,
    });
}

export function createEmoteAnimations(scene: Phaser.Scene): void {
  for (const id of EMOTE_IDS) {
    const key = emoteAnimationKey(id);
    if (scene.anims.exists(key) || !scene.textures.exists(emoteTextureKey(id))) continue;
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(emoteTextureKey(id), {
        start: 0,
        end: EMOTE_SHEETS[id].frames - 1,
      }),
      frameRate: EMOTE_SHEETS[id].fps,
      repeat: -1,
    });
  }
}

type EmoteListener = (event: { sessionId: string; emote: EmoteId }) => void;
const listeners = new Set<EmoteListener>();

/** Emotes from the server, for the world scene. */
export const emoteEvents = {
  emit(event: { sessionId: string; emote: EmoteId }): void {
    for (const listener of listeners) listener(event);
  },
  on(listener: EmoteListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};
